
import { getElectronAPI }        from "@/plugins/desktop";
import NfceRejeicaoService       from "../services/NfceRejeicaoService";
import SyncPdvDesktopService     from "../services/SyncPdvDesktopService";

export default {
  name: "CorrigirFiscalModal",

  props: {
    /** Registro nfce_local (deve ter uuid_venda_terminal, motivo_erro, codigo_rejeicao etc.) */
    nfce: {
      type:     Object,
      required: true,
    },
  },

  emits: ["fechar", "atualizar"],

  data() {
    return {
      carregando: false,
      salvando:   false,
      regenerando: false,
      transmitindo: false,
      erro:    null,
      sucesso: null,
      itens:   [],
      nItemSelecionado: 1,
      form: {
        csosn:     "102",
        cfop:      "",
        ncm:       "",
        cst_pis:   "07",
        cst_cofins: "07",
      },
      correcaoSalva: false,
      xmlPronto:     false,
      csosnOpcoes:   NfceRejeicaoService.csosnSugeridos(),
    };
  },

  computed: {
    rejeicao() {
      const motivo = this.nfce.motivo_erro || this.nfce.ultimo_erro || null;
      if (!motivo) return null;
      return NfceRejeicaoService.parse(motivo);
    },

    itemAtual() {
      if (!this.itens.length) return null;
      const idx = Math.max(0, Number(this.nItemSelecionado) - 1);
      return this.itens[idx] ?? this.itens[0] ?? null;
    },
  },

  async created() {
    await this._carregarItens();
  },

  methods: {
    fmtBRL(v) {
      return Number(v || 0).toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    },

    async _carregarItens() {
      this.carregando = true;
      try {
        const api  = getElectronAPI();
        this.itens = (await api.nfce.getItens(this.nfce.uuid_venda_terminal)) || [];

        // Se a rejeição identificou o nItem, pré-seleciona e preenche o form
        const r = this.rejeicao;
        if (r && r.nItem != null) {
          this.nItemSelecionado = r.nItem;
        } else {
          this.nItemSelecionado = 1;
        }

        this._preencherFormComItem();
      } catch (e) {
        this.erro = `Erro ao carregar itens: ${e.message}`;
      } finally {
        this.carregando = false;
      }
    },

    _preencherFormComItem() {
      const item = this.itemAtual;
      if (!item) return;

      // Tenta extrair dados fiscais do payload_json
      let fiscal = {};
      try {
        const p = item.payload_json ? JSON.parse(item.payload_json) : {};
        fiscal  = p.fiscal ?? p._fiscal ?? p.dados_fiscais ?? p;
      } catch (_) { /* ignore parse errors */ }

      this.form.csosn     = item.cst_csosn_codigo  || fiscal.csosn     || "102";
      this.form.cfop      = item.cfop_codigo        || fiscal.cfop      || "";
      this.form.ncm       = item.ncm_codigo         || fiscal.ncm       || "";
      this.form.cst_pis   = item.cst_pis_codigo     || fiscal.cst_pis   || "07";
      this.form.cst_cofins = item.cst_cofins_codigo || fiscal.cst_cofins || "07";

      // Se modelo 65 e CSOSN for 1 (indevido), sugere 102
      const modelo = Number(this.nfce.modelo || 65);
      if (modelo === 65 && ["1", "01", "001"].includes(String(this.form.csosn))) {
        this.form.csosn = "102";
      }
    },

    async salvarCorrecao() {
      this.salvando = true;
      this.erro     = null;
      this.sucesso  = null;
      try {
        const api = getElectronAPI();

        // 1. Corrige os dados fiscais do item
        await api.nfce.corrigirItemFiscal(
          this.nfce.uuid_venda_terminal,
          this.nItemSelecionado,
          {
            csosn:      this.form.csosn     || null,
            cfop:       this.form.cfop      || null,
            ncm:        this.form.ncm       || null,
            cst_pis:    this.form.cst_pis   || null,
            cst_cofins: this.form.cst_cofins || null,
          }
        );

        // 2. Prepara a NFC-e para regerar (limpa xml, status → pendente_xml)
        await api.nfce.prepararParaRegerarXml(this.nfce.uuid_venda_terminal);

        this.sucesso      = "Correção salva. Agora gere e assine o XML novamente.";
        this.correcaoSalva = true;
        this.xmlPronto    = false;
        this.$emit("atualizar");
      } catch (e) {
        this.erro = `Erro ao salvar correção: ${e.message}`;
      } finally {
        this.salvando = false;
      }
    },

    async gerarEAssinar() {
      this.regenerando = true;
      this.erro        = null;
      this.sucesso     = null;
      try {
        const api    = getElectronAPI();
        const uuid   = this.nfce.uuid_venda_terminal;

        // Gerar XML
        await api.nfce.gerarXml(uuid);

        // Assinar XML
        await api.nfce.assinarXml(uuid);

        this.sucesso   = "XML gerado e assinado com sucesso! Agora transmita novamente.";
        this.xmlPronto = true;
        this.$emit("atualizar");
      } catch (e) {
        this.erro = `Erro ao gerar/assinar XML: ${e.message}`;
      } finally {
        this.regenerando = false;
      }
    },

    async transmitir() {
      this.transmitindo = true;
      this.erro         = null;
      this.sucesso      = null;
      try {
        const resultado = await SyncPdvDesktopService.transmitirNfce(
          this.nfce.uuid_venda_terminal
        );
        const status = resultado?.status;
        if (status === "autorizada" || status === "ja_autorizada") {
          this.sucesso = `NFC-e autorizada! Protocolo: ${resultado.protocolo || "—"}`;
          this.xmlPronto     = false;
          this.correcaoSalva = false;
          this.$emit("atualizar");
        } else if (status === "rejeitada") {
          this.erro = `Ainda rejeitada: ${resultado.motivo || resultado.codigo || "sem motivo"}`;
          this.$emit("atualizar");
        } else {
          this.erro = `Transmissão com status inesperado: ${status}`;
        }
      } catch (e) {
        this.erro = `Erro ao transmitir: ${e.message}`;
      } finally {
        this.transmitindo = false;
      }
    },
  },
};
